import { useCallback, useMemo, useState } from 'react'
import type { IFilterDataArray } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { Button } from 'react-bootstrap'
import { useFieldArray } from 'react-hook-form'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import AddSegmentsModal from 'components/configuration/components/lms-profiles/AddSegmentsModal'
import { SegmentsActions } from 'pages/configuration/LmsProfiles/components/segments'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { prefixSegments } from 'pages/configuration/LmsProfiles/constants'
import { Segment } from 'models/segment.model'
import { useCreateProfile } from '../../../../context'
import Table from './components/Table'
import { Wrapper, HeaderActions } from './styled'

export const Segments = () => {
  const [open, setOpen] = useState(false)

  const { control } = useCreateProfile()

  const { fields, append, remove, replace } = useFieldArray({
    name: 'segments',
    control,
  })

  const users = useMemo(() => fields.map((field) => field.segmentId), [fields])

  const existingSegmentIds = useMemo(() => new Set(users), [users])

  const onAddUsers = useCallback(
    (newUsers: Segment[]) => {
      const toAppend = newUsers
        .map((s) => s.id)
        .filter((id) => !existingSegmentIds.has(id))
      if (!toAppend.length) return
      append(toAppend.map((segmentId) => ({ segmentId })))
    },
    [append, existingSegmentIds]
  )

  const removeBySegmentId = useCallback(
    (segmentId: number) => {
      const idx = fields.findIndex((f) => f.segmentId === segmentId)
      if (idx >= 0) remove(idx)
    },
    [fields, remove]
  )

  const filter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'id',
        condition: 'and',
        operator: 'not_in',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: users,
      },
    ],
    [users]
  )

  const tRelative = useTranslationRankmi({
    prefix: prefixSegments,
  })

  const [selectedUsers, setSelectedUsers] = useState<number[]>([])

  return (
    <div className="mt-3">
      <AddSegmentsModal
        show={open}
        onClose={() => setOpen(false)}
        onConfirm={(newSegments) => {
          onAddUsers(newSegments)
          setOpen(false)
        }}
        filter={filter}
      />

      <Wrapper>
        <Table
          users={users}
          onRemove={removeBySegmentId}
          selected={selectedUsers}
          setSelected={setSelectedUsers}
          actions={
            <HeaderActions className="d-flex justify-content-end">
              <Button size="sm" onClick={() => setOpen(true)} className="mr-2">
                <FontAwesomeIcon icon={faPlus} />
                {tRelative('addSegments')}
              </Button>
              <SegmentsActions
                selected={selectedUsers.length}
                onRemoveSelected={() => {
                  const selectedIds = new Set(selectedUsers)
                  replace(
                    fields.filter(
                      ({ segmentId }) => !selectedIds.has(segmentId)
                    )
                  )
                  setSelectedUsers([])
                }}
              />
            </HeaderActions>
          }
        />
      </Wrapper>
    </div>
  )
}
